import { installPolicyOwnerGates } from './chunk-FNREXSA5.js';
import { createTickScheduler, EventBus, createUnifiedLoader, preprocessSchema, parseOrbitalTraits, StateMachineManager, MountLifecycle, seedBrowserStore, parseDurationString, dispatchDeclaredInput, UNMOUNT_EVENT, LIFECYCLE_EVENTS, evaluateOrbitalEvent, buildTraitIndexForOrbital, InFlightCalls, deliveryOf, collectListenerTargets, freshDeliveryGuardBindings, findInitialState, readTraitFrame, appNavItems, sigilThemeKey, runServerEffectStage, eventRouteKey } from './chunk-KF5Y7WEB.js';
export { collectDeclaredConfigDefaults, inlineNavItems } from './chunk-KF5Y7WEB.js';
import './chunk-JCTUAEMM.js';
import './chunk-SLXJYKVO.js';
import { isValidCronExpression } from './chunk-TJQWHYNZ.js';
import { createContextFromBindings, applyRowAccess } from './chunk-LPUBGPML.js';
import './chunk-MLKGABMK.js';
import { identitySourceOf, resolveViewer } from '@almadar/auth';
import { createLogger } from '@almadar/logger';
import * as nodeModule from 'module';
import { evaluateGuard } from '@almadar/evaluator';
import { ownerFieldsFromSchema, identityEntityName, identityEntitiesOf, entityAccessPolicies } from '@almadar/core/mock';
import { DEFAULT_VIEWER, themeDataKey, buildResolvedTraitConfigs, personaFromIdentityRow, resolveDefaultViewer, collectCallsiteCaptureChildren, isEntityCall, externalInputsOf, readableEntitiesOf, normalizeUserContext, dispatchVisitKey, traitsEmbeddedByEffects } from '@almadar/core';
import { MockPersistenceAdapter, InMemoryPersistence } from '@almadar/db/mock';

var _resolvedNodeRequire = null;
function nodeRequire(modulePath) {
  if (!_resolvedNodeRequire) {
    const evalRequire = (0, eval)('typeof require !== "undefined" ? require : null');
    if (evalRequire) {
      _resolvedNodeRequire = evalRequire;
    } else {
      const createReq = nodeModule.createRequire;
      if (typeof createReq !== "function") {
        throw new Error(
          "[OrbitalServerRuntime] No synchronous require available. This branch is Node-only \u2014 invoking it from a browser indicates an isNodeEnv() guard regression upstream."
        );
      }
      _resolvedNodeRequire = createReq(import.meta.url);
    }
  }
  return _resolvedNodeRequire(modulePath);
}
var effectLog = createLogger("almadar:runtime:effects");
var busLog = createLogger("almadar:runtime:bus");
var renderLog = createLogger("almadar:runtime:render-ui");
var xOrbitalLog = createLogger("almadar:runtime:cross-orbital");
var persistLog = createLogger("almadar:runtime:persist");
var registerLog = createLogger("almadar:runtime:register");
var CROSS_ORBITAL_RELAY_CAP = 500;
function isNodeEnv() {
  return typeof process !== "undefined" && Boolean(process.versions?.node);
}
var ANONYMOUS_CLIENT = "\0anonymous";
function needsPreprocessing(schema) {
  for (const orbital of schema.orbitals) {
    if (orbital.uses && orbital.uses.length > 0) {
      return true;
    }
    for (const t of orbital.traits ?? []) {
      if (typeof t === "object" && "ref" in t && t.ref.includes(".")) {
        return true;
      }
    }
  }
  return false;
}
async function readSidecarCatalogs(readFile, orbPath, locales) {
  const messages = {};
  const stem = orbPath.replace(/\.orb$/, "");
  for (const locale of locales) {
    const catalogPath = `${stem}.${locale}.json`;
    let catalogRaw;
    try {
      catalogRaw = await readFile(catalogPath, "utf-8");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      throw new Error(`registerFromFile: ${orbPath} declares locale "${locale}" but its catalog ${catalogPath} could not be read: ${msg}`);
    }
    messages[locale] = JSON.parse(catalogRaw);
  }
  return messages;
}
var OrbitalServerRuntime = class {
  orbitals = /* @__PURE__ */ new Map();
  eventBus;
  config;
  /** The bound persistence adapter (mock/in-memory/consumer-supplied). Public
   *  so a test can inspect committed rows honestly — `(runtime as any)
   *  .persistence` was the alternative, and that cast is what this field
   *  visibility replaces. */
  persistence;
  tickBindings = [];
  /** `orbital::trait` → the clients that have it mounted (Runtime Spec Clause 8.3). */
  mountedBy = /* @__PURE__ */ new Map();
  // One coalesced clock for every tick this runtime registers — replaces
  // "one setInterval per tick" with a single accumulator loop so ticks due
  // in the same pass fire together instead of on independent timers.
  tickScheduler = createTickScheduler();
  loader = null;
  preprocessedCache = /* @__PURE__ */ new Map();
  transitionObservers = /* @__PURE__ */ new Set();
  transitionFanout = {
    onTransition: (trace) => {
      for (const o of this.transitionObservers) o.onTransition(trace);
    },
    onRollback: (trace) => {
      for (const o of this.transitionObservers) o.onRollback?.(trace);
    }
  };
  entitySharingMap = {};
  eventNamespaceMap = {};
  osHandlers = null;
  osHandlersPromise = null;
  programHandlersPromise = null;
  resolvedSchema = null;
  /** App-level theme key from the schema's `theme "<key>"` header — the
   *  `@currentTheme` fallback between an orbital's own `theme` and
   *  `DEFAULT_THEME_KEY`. Set at registration, before the orbital loops
   *  (`resolvedSchema` is assigned only after them). */
  appThemeKey;
  /** The program's declared `locales`, in declared order; the first is the default. */
  appLocales = [];
  /** Merged message catalogs of every registered program (keys are behavior-qualified). */
  messages = {};
  /** Wired by the hosting server (e.g. the playground SSE endpoint) via `setLiveBroadcastSink`. */
  liveBroadcastSink = null;
  hostDispatchSink = null;
  /**
   * Tick-broadcast relay (T6): newest pending item per
   * (originClientId, orbital, event), flushed to the live-broadcast sink on
   * the `tickRelayIntervalMs` cadence — other tabs get the latest position
   * at snapshot rate, never 1:1 with emissions.
   */
  tickRelayPending = /* @__PURE__ */ new Map();
  tickRelayTimer = null;
  /**
   * Trait name -> resolved `TraitConfig`, computed once per `register()` via
   * `buildResolvedTraitConfigs` (`@almadar/core`). An embedded sub-trait's
   * OWN declared config schema (`registered.traits` / `collectDeclaredConfigDefaults`)
   * carries `@config.X` forwards verbatim — this map chains those through to
   * the trait that actually embeds it (see `embedded-trait-config.ts` for the
   * full rationale). Used as a fallback layer in `buildBindingContext` behind
   * `declaredDefaults` and ahead of `callSiteOverride`.
   */
  resolvedTraitConfigs = {};
  /**
   * Referrer trait name → the DIRECT children (via `@trait.X`) that need
   * their lifecycle transition re-run under the referrer's `callsitePayload`
   * whenever the referrer's own transition fires — computed once per
   * `register()` via `@almadar/core`'s `collectCallsiteCaptureChildren`
   * (merged across every orbital in the schema, same flattening
   * `resolvedTraitConfigs` uses). See `rerenderCallsiteCaptureChildren`.
   */
  callsiteCaptureChildrenByTrait = /* @__PURE__ */ new Map();
  /** Embedded child → the payload its composer last composed it with (sticky `@callsitePayload`). */
  composedCallsitePayloads = /* @__PURE__ */ new Map();
  constructor(config = {}) {
    this.config = {
      mode: "mock",
      // Default to mock mode for preview
      autoPreprocess: false,
      namespaceEvents: true,
      ...config,
      // `@user` is never Null. A host that named no viewer (no ALMADAR_PERSONA,
      // no auth yet) otherwise renders every `viewerName: @user.name` blank, so
      // the app cannot say who you are. DEFAULT_VIEWER carries an empty `role`,
      // so no `@user.role` guard changes outcome — see its doc in @almadar/core.
      defaultUser: config.defaultUser ?? DEFAULT_VIEWER
    };
    this.viewerNamedByHost = config.defaultUser !== void 0;
    this.eventBus = new EventBus();
    if (config.loaderConfig?.loader) {
      this.loader = config.loaderConfig.loader;
    } else if (config.loaderConfig?.stdLibPath) {
      this.loader = createUnifiedLoader({
        basePath: config.loaderConfig.basePath,
        stdLibPath: config.loaderConfig.stdLibPath,
        scopedPaths: config.loaderConfig.scopedPaths
      });
    }
    if (this.config.mode === "mock" && !config.persistence) {
      this.persistence = new MockPersistenceAdapter({
        seed: config.mockSeed,
        defaultSeedCount: config.mockSeedCount ?? 6,
        debug: config.debug,
        // Let the dev viewer own some seeded rows, so ownership-scoped views
        // ("only mine") have data instead of being indistinguishable from a
        // broken filter. Columns are declared, never inferred by name.
        // `this.config`, not `config` — the DEFAULT_VIEWER fallback is applied
        // there, and reading the raw param leaves every seeded row unowned.
        ownerId: this.config.defaultUser?.id,
        ownerFields: config.mockOwnerFields
      });
      if (config.debug) {
        persistLog.debug("mock:init", { adapter: "MockPersistenceAdapter" });
      }
    } else {
      this.persistence = config.persistence || new InMemoryPersistence();
    }
    this.osHandlers = { handlers: {}, cleanup: () => {
    } };
  }
  /**
   * Lazily wire the OS-level effect handlers (fs/net/child_process), merging
   * them UNDER any user-provided handlers. Deferred out of the constructor
   * because `../effects/createOsHandlers.js` is ESM and `require()`-ing it from a
   * `type: module` package throws ERR_REQUIRE_ESM — so it is dynamic-import()ed
   * here on the first event. Node-only, idempotent (single shared load), and a
   * no-op in the browser (the import never runs behind the isNodeEnv guard).
   */
  async ensureOsHandlers() {
    if (!isNodeEnv()) return;
    if (!this.osHandlersPromise) {
      this.osHandlersPromise = (async () => {
        const { createOsHandlers } = await import('./createOsHandlers.js');
        this.osHandlers = createOsHandlers({
          emitEvent: (type, payload) => this.dispatchExternalEvent(type, payload)
        });
        this.config.effectHandlers = {
          ...this.osHandlers.handlers,
          ...this.config.effectHandlers
        };
      })();
    }
    return this.osHandlersPromise;
  }
  /**
   * Lazily wire the program host handlers (behavior/* and program/* effects),
   * merging them UNDER any user-provided handlers. Only when `programHost`
   * is configured; otherwise the effects fail explicitly.
   */
  async ensureProgramHandlers() {
    if (!isNodeEnv()) return;
    const host = this.config.programHost;
    if (host === void 0) return;
    if (!this.programHandlersPromise) {
      this.programHandlersPromise = (async () => {
        const { createProgramHandlers } = await import('./createProgramHandlers.js');
        this.config.effectHandlers = {
          ...createProgramHandlers(host),
          ...this.config.effectHandlers
        };
      })();
    }
    return this.programHandlersPromise;
  }
  /**
   * Lazily construct a default loader when the caller didn't provide one
   * but `register()` needs to preprocess. Looks for `@almadar/std` in the
   * nearest `node_modules` so cross-orbital `std/behaviors/<name>` imports
   * resolve to the tiered registry on disk.
   *
   * Node only — browsers should receive already-preprocessed schemas from
   * their server.
   */
  async ensureLoader() {
    if (this.loader) return;
    if (typeof process === "undefined" || !process.versions?.node) {
      return;
    }
    try {
      const [{ fileURLToPath }, path, fs] = await Promise.all([
        import('url'),
        import('path'),
        import('fs')
      ]);
      const mainEntryUrl = import.meta.resolve("@almadar/std");
      const mainEntry = fileURLToPath(mainEntryUrl);
      let stdLibPath = path.dirname(mainEntry);
      while (stdLibPath !== path.dirname(stdLibPath)) {
        if (fs.existsSync(path.join(stdLibPath, "package.json"))) {
          const pkg = JSON.parse(
            fs.readFileSync(path.join(stdLibPath, "package.json"), "utf-8")
          );
          if (pkg.name === "@almadar/std") break;
        }
        stdLibPath = path.dirname(stdLibPath);
      }
      const basePath = this.config.loaderConfig?.basePath ?? process.cwd();
      this.loader = createUnifiedLoader({
        basePath,
        stdLibPath,
        scopedPaths: this.config.loaderConfig?.scopedPaths
      });
      if (this.config.debug) {
        registerLog.debug("loader:constructed", { basePath, stdLibPath });
      }
    } catch (err) {
      if (this.config.debug) {
        registerLog.warn("loader:construct-failed", { error: err instanceof Error ? err : String(err) });
      }
    }
  }
  // ==========================================================================
  // Schema Registration
  // ==========================================================================
  /**
   * Register an OrbitalSchema for execution.
   *
   * Auto-preprocesses the schema when it contains `uses` declarations or
   * unresolved cross-orbital trait references (e.g. a trait with
   * `ref: "Modal.traits.ModalRecordModal"` and no inline `stateMachine`).
   * Without preprocessing, those refs arrive empty at the state machine and
   * button clicks silently do nothing — see Phase 9.5.H.
   *
   * Preprocessing needs a loader. If `loaderConfig` is set, that loader is
   * used. Otherwise, a default loader is constructed that points at
   * `<cwd>` (for `basePath`) and the nearest `node_modules/@almadar/std` (for
   * `stdLibPath`), which matches how every caller in this monorepo has the
   * std registry on disk.
   */
  /**
   * The in-flight (or last-completed) app mutation — `register()` chains its
   * body onto this, and `unregisterAll()` stamps a marker after clearing.
   * `processOrbitalEvent` consults it when the requested orbital is missing:
   * during a catalog-style app swap the new schema's registration is already
   * in flight while a client's first events arrive, and answering those with
   * an immediate `Orbital not found` 404 (the pre-fix behavior) races the
   * swap — big multi-orbital organisms lost their mount INITs intermittently
   * while single-orbital ones almost always won. Awaiting the epoch and
   * re-reading the map turns the race into a brief wait. `BrowserPlayground`
   * gated dispatches on registration ad hoc for the same race; the epoch is
   * the canonical, host-wide fix.
   */
  registrationEpoch = null;
  /**
   * Reentrancy guard for `withRegistrationMutation`. While a bracket is
   * open, `register()`/`unregisterAll()` must NOT chain/stamp the epoch:
   * the callback's sequential awaits already order them, and chaining a
   * `register()` awaited by the callback onto the bracket's gate would
   * deadlock (callback → register → epoch → gate → callback's finally).
   */
  registrationMutationDepth = 0;
  async register(schema, options = {}) {
    if (this.registrationMutationDepth > 0) {
      await this.doRegister(schema, options);
      return;
    }
    const previous = this.registrationEpoch ?? Promise.resolve();
    const epoch = previous.then(
      () => this.doRegister(schema, options),
      () => this.doRegister(schema, options)
    );
    this.registrationEpoch = epoch;
    await epoch;
  }
  async doRegister(schema, options) {
    if (this.config.debug) {
      registerLog.debug("register:schema", { name: schema.name });
    }
    this.appThemeKey = themeDataKey(schema.theme) || void 0;
    this.appLocales = schema.locales ?? [];
    for (const [locale, catalog] of Object.entries(options.messages ?? {})) {
      this.messages[locale] = { ...this.messages[locale], ...catalog };
    }
    if (needsPreprocessing(schema)) {
      await this.ensureLoader();
      if (this.loader) {
        if (this.config.debug) {
          registerLog.debug("register:auto-preprocessing", { name: schema.name });
        }
        const result = await preprocessSchema(schema, {
          basePath: this.config.loaderConfig?.basePath || process.cwd(),
          stdLibPath: this.config.loaderConfig?.stdLibPath,
          scopedPaths: this.config.loaderConfig?.scopedPaths,
          loader: this.loader,
          namespaceEvents: this.config.namespaceEvents
        });
        if (!result.success) {
          throw new Error(
            `Schema preprocessing failed: ${result.errors.join("; ")}`
          );
        }
        schema = result.data.schema;
        this.entitySharingMap = {
          ...this.entitySharingMap,
          ...result.data.entitySharing
        };
        this.eventNamespaceMap = {
          ...this.eventNamespaceMap,
          ...result.data.eventNamespaces
        };
      } else if (this.config.debug) {
        registerLog.warn("register:no-loader", { name: schema.name });
      }
    }
    this.prepareMockSeeding(schema);
    const lazyOrbitals = (options.lazy ?? []).flatMap((lazy) => lazy.orbitals);
    const executed = lazyOrbitals.length > 0 ? { ...schema, orbitals: [...schema.orbitals, ...lazyOrbitals] } : schema;
    for (const orbital of executed.orbitals) {
      await this.registerOrbitalAsync(orbital);
    }
    this.setupTicks();
    this.resolvedSchema = schema;
    this.resolvedTraitConfigs = buildResolvedTraitConfigs(executed);
    this.callsiteCaptureChildrenByTrait = this.buildCallsiteCaptureChildrenByTrait(executed);
    this.flushPendingOwnerRestamp();
  }
  /**
   * Everything the mock seeder needs before the first entity seeds: the owner
   * columns derived from the `[identity]` entity (twin of `seed.rs`
   * `owner_fields_from_schema`), the `@create` owner gate, and the shared seed
   * order's first step, the `[identity]` entity itself — its rows are the
   * persona roster and the first draws of the seeded stream on every path
   * (`seed.rs` `seed_sample_data_as`, the compiled `seedMockData.ts`).
   */
  prepareMockSeeding(schema) {
    if (!(this.persistence instanceof MockPersistenceAdapter)) return;
    const derived = ownerFieldsFromSchema(schema);
    if (derived.length > 0) {
      this.persistence.addOwnerFields(derived);
      persistLog.debug("mock:identity-owner-fields", {
        identity: identityEntityName(schema),
        columns: derived
      });
    }
    this.installOwnerGate(schema);
    if (!this.viewerNamedByHost) this.persistence.restampOwner(void 0);
    this.seedIdentityEntity(schema);
    if (!this.viewerNamedByHost) {
      const identity = identityEntitiesOf(schema.orbitals ?? [])[0]?.name;
      const roster = identity ? this.persistence.rowsOf(identity).map((row) => personaFromIdentityRow(row)).filter((p) => p !== void 0) : [];
      const viewer = resolveDefaultViewer(roster);
      this.config.defaultUser = viewer;
      this.persistence.restampOwner(viewer.id);
    }
  }
  seedIdentityEntity(schema) {
    if (this.config.mode !== "mock" || !(this.persistence instanceof MockPersistenceAdapter)) return;
    const identity = identityEntitiesOf(schema.orbitals ?? [])[0];
    if (!identity?.name || !identity.fields) return;
    if (identity.instances && identity.instances.length > 0) return;
    this.persistence.registerEntity({
      name: identity.name,
      id: identity.id,
      collection: identity.collection,
      fields: identity.fields.filter((f) => typeof f.name === "string" && f.name.length > 0),
      persistence: identity.persistence,
      identity: true
    });
  }
  /**
   * Merge `collectCallsiteCaptureChildren` across every orbital in the
   * schema into ONE flat referrer-trait-name → children map — same
   * flattening `resolvedTraitConfigs` uses, safe because trait names are
   * unique within one running schema (the compose/resolve pipeline already
   * relies on that for `configByTrait` and `resolvedTraitConfigs`).
   */
  buildCallsiteCaptureChildrenByTrait(schema) {
    const merged = /* @__PURE__ */ new Map();
    for (const orbital of schema.orbitals) {
      for (const [referrer, children] of collectCallsiteCaptureChildren(orbital)) {
        merged.set(referrer, children);
      }
    }
    return merged;
  }
  /**
   * Register an OrbitalSchema synchronously (for backward compatibility).
   * Note: This version doesn't wait for instance seeding to complete.
   * Use async register() for guaranteed instance seeding.
   */
  registerSync(schema) {
    if (this.config.debug) {
      registerLog.debug("register:schema-sync", { name: schema.name });
    }
    this.appThemeKey = themeDataKey(schema.theme) || void 0;
    this.prepareMockSeeding(schema);
    for (const orbital of schema.orbitals) {
      this.registerOrbital(orbital);
    }
    this.setupTicks();
    this.resolvedSchema = schema;
    this.resolvedTraitConfigs = buildResolvedTraitConfigs(schema);
    this.callsiteCaptureChildrenByTrait = this.buildCallsiteCaptureChildrenByTrait(schema);
    this.flushPendingOwnerRestamp();
  }
  /**
   * Returns the schema that this runtime is currently executing, post-
   * preprocessing. Safe to expose from an HTTP `/api/schema` endpoint — every
   * cross-orbital trait ref will have an inline `stateMachine` already, which
   * is what the browser's `schema-to-ir` resolver needs to wire button clicks
   * back to state transitions.
   *
   * Returns `null` if `register()` hasn't run yet.
   */
  /** The registered program's name, or null when nothing is registered (none yet, or after `unregisterAll`). */
  registeredSchemaName() {
    return this.orbitals.size > 0 ? this.resolvedSchema?.name ?? null : null;
  }
  getResolvedSchema() {
    return this.resolvedSchema;
  }
  /** The registered program's message catalogs (locale → qualified key → message), for a host's client. */
  getMessages() {
    return this.messages;
  }
  /**
   * One-call entry point: read an `.orb` file from disk, parse it, preprocess
   * cross-orbital imports, and register the result. Callers never touch raw
   * `.orb` bytes — `register()` handles preprocessing internally.
   *
   * Node only. Browsers must receive already-resolved schemas from their
   * server (see `getResolvedSchema()`).
   */
  async registerFromFile(path) {
    if (typeof process === "undefined" || !process.versions?.node) {
      throw new Error(
        "registerFromFile is Node-only. Browsers should receive resolved schemas from their server."
      );
    }
    const { readFile } = await import('fs/promises');
    const raw = await readFile(path, "utf-8");
    let schema;
    try {
      schema = JSON.parse(raw);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      throw new Error(`registerFromFile: ${path} is not valid JSON: ${msg}`);
    }
    const locales = schema.locales ?? [];
    const messages = await readSidecarCatalogs(readFile, path, locales);
    const { dirname, join } = await import('path');
    const lazy = [];
    for (const page of schema.lazyPages ?? []) {
      const lazyPath = join(dirname(path), page.orbRef);
      let lazyRaw;
      try {
        lazyRaw = await readFile(lazyPath, "utf-8");
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        throw new Error(`registerFromFile: ${path} declares lazy page ${page.path} but its behavior ${page.orbRef} could not be read: ${msg}`);
      }
      const lazySchema = JSON.parse(lazyRaw);
      if (!lazySchema.orbitals.some((o) => o.name === page.orbital)) {
        throw new Error(`registerFromFile: lazy page ${page.path} expects orbital "${page.orbital}" in ${page.orbRef}, found: ${lazySchema.orbitals.map((o) => o.name).join(", ")}`);
      }
      lazy.push(lazySchema);
      for (const [locale, catalog] of Object.entries(await readSidecarCatalogs(readFile, lazyPath, locales))) {
        messages[locale] = { ...messages[locale], ...catalog };
      }
    }
    await this.register(schema, { messages, lazy });
  }
  /**
   * Register an OrbitalSchema with preprocessing to resolve `uses` imports.
   *
   * This method:
   * 1. Loads all external orbitals referenced in `uses` declarations
   * 2. Expands entity/trait/page references to inline definitions
   * 3. Builds entity sharing and event namespace maps
   * 4. Caches the preprocessed result
   * 5. Registers the resolved schema
   *
   * @param schema - Schema with potential `uses` declarations
   * @param options - Optional preprocessing options
   * @returns Preprocessing result with entity sharing info
   *
   * @example
   * ```typescript
   * const runtime = new OrbitalServerRuntime({
   *   loaderConfig: {
   *     basePath: '/schemas',
   *     stdLibPath: '/std',
   *   },
   * });
   *
   * const result = await runtime.registerWithPreprocess(schema);
   * if (result.success) {
   *   console.log('Registered with', Object.keys(result.entitySharing).length, 'orbitals');
   * }
   * ```
   */
  async registerWithPreprocess(schema, options) {
    if (!this.loader && !this.config.loaderConfig) {
      return {
        success: false,
        errors: ["Loader not configured. Set loaderConfig in OrbitalServerRuntimeConfig."]
      };
    }
    if (!this.loader && this.config.loaderConfig) {
      this.loader = this.config.loaderConfig.loader ?? createUnifiedLoader({
        basePath: this.config.loaderConfig.basePath,
        stdLibPath: this.config.loaderConfig.stdLibPath,
        scopedPaths: this.config.loaderConfig.scopedPaths
      });
    }
    const cacheKey = `${schema.name}:${schema.version || "1.0.0"}`;
    const cached = this.preprocessedCache.get(cacheKey);
    if (cached) {
      if (this.config.debug) {
        registerLog.debug("preprocess:cache-hit", { name: schema.name });
      }
      this.register(cached.schema);
      this.entitySharingMap = { ...this.entitySharingMap, ...cached.entitySharing };
      this.eventNamespaceMap = { ...this.eventNamespaceMap, ...cached.eventNamespaces };
      return {
        success: true,
        entitySharing: cached.entitySharing,
        eventNamespaces: cached.eventNamespaces,
        warnings: cached.warnings
      };
    }
    if (this.config.debug) {
      registerLog.debug("preprocess:start", { name: schema.name });
    }
    const result = await preprocessSchema(schema, {
      basePath: this.config.loaderConfig?.basePath || ".",
      stdLibPath: this.config.loaderConfig?.stdLibPath,
      scopedPaths: this.config.loaderConfig?.scopedPaths,
      loader: this.loader,
      namespaceEvents: this.config.namespaceEvents
    });
    if (!result.success) {
      return {
        success: false,
        errors: result.errors
      };
    }
    this.preprocessedCache.set(cacheKey, result.data);
    this.entitySharingMap = { ...this.entitySharingMap, ...result.data.entitySharing };
    this.eventNamespaceMap = { ...this.eventNamespaceMap, ...result.data.eventNamespaces };
    this.register(result.data.schema);
    return {
      success: true,
      entitySharing: result.data.entitySharing,
      eventNamespaces: result.data.eventNamespaces,
      warnings: result.data.warnings
    };
  }
  /**
   * Get entity sharing information for registered orbitals.
   * Useful for determining entity isolation and collection names.
   */
  getEntitySharing() {
    return { ...this.entitySharingMap };
  }
  /**
   * Get event namespace mapping for registered orbitals.
   * Useful for debugging cross-orbital event routing.
   */
  getEventNamespaces() {
    return { ...this.eventNamespaceMap };
  }
  /**
   * Clear the preprocessing cache.
   */
  clearPreprocessCache() {
    this.preprocessedCache.clear();
  }
  /**
   * Register a single orbital
   */
  async registerOrbitalAsync(orbital) {
    const { traits: traitDefs, inlineTraits, configByTrait, entity } = parseOrbitalTraits(orbital);
    const manager = new StateMachineManager(traitDefs, {
      contextExtensions: this.config.contextExtensions
    }, this.transitionFanout);
    for (const [traitName, traitConfig] of configByTrait) {
      manager.setTraitConfig(traitName, traitConfig);
    }
    this.orbitals.set(orbital.name, {
      schema: orbital,
      entity,
      traits: inlineTraits,
      configByTrait,
      manager,
      entityData: /* @__PURE__ */ new Map(),
      traitFieldStates: /* @__PURE__ */ new Map(),
      mount: new MountLifecycle()
    });
    for (const other of this.orbitals.values()) {
      other.traitIndex = void 0;
    }
    if (entity?.local === true && !(this.config.mode === "mock" && this.persistence instanceof MockPersistenceAdapter)) {
      await seedBrowserStore(this.persistence, [entity]);
    } else if (entity?.name && entity.instances && Array.isArray(entity.instances)) {
      const instances = entity.instances;
      if (instances.length > 0) {
        persistLog.debug("seed:start", { entity: entity.name, count: instances.length });
        const results = await Promise.all(
          instances.map(async (instance) => {
            try {
              const result = await this.persistence.create(entity.name, instance);
              persistLog.debug("seed:instance", { entity: entity.name, id: instance.id ?? "no-id" });
              return result;
            } catch (err) {
              persistLog.error("seed:instance-error", {
                entity: entity.name,
                id: instance.id,
                error: err instanceof Error ? err : String(err)
              });
              return null;
            }
          })
        );
        const successCount = results.filter((r) => r !== null).length;
        persistLog.debug("seed:done", { entity: entity.name, success: successCount, total: instances.length });
      }
    } else if (this.config.mode === "mock" && this.persistence instanceof MockPersistenceAdapter && (entity?.local !== true || entity.seedMock === true)) {
      if (this.config.debug) {
        persistLog.debug("mock:generate", { entity: entity?.name });
      }
      if (entity?.name && entity.fields) {
        const fields = entity.fields.filter(
          (f) => typeof f.name === "string" && f.name.length > 0
        );
        this.persistence.registerEntity({
          name: entity.name,
          id: entity.id,
          collection: entity.collection,
          fields,
          persistence: entity.persistence,
          identity: entity.identity
        });
        if (this.config.debug) {
          persistLog.debug("mock:seeded", { entity: entity.name, count: this.persistence.count(entity.name) });
        }
      }
    }
    const auxiliaryEntities = orbital.auxiliaryEntities;
    if (auxiliaryEntities !== void 0 && !(this.config.mode === "mock" && this.persistence instanceof MockPersistenceAdapter)) {
      const localAux = auxiliaryEntities.filter((ref) => typeof ref !== "string" && !isEntityCall(ref) && ref.local === true);
      await seedBrowserStore(this.persistence, localAux);
    }
    if (auxiliaryEntities !== void 0 && auxiliaryEntities.length > 0 && this.config.mode === "mock" && this.persistence instanceof MockPersistenceAdapter) {
      for (const auxRef of auxiliaryEntities) {
        if (typeof auxRef === "string" || isEntityCall(auxRef)) continue;
        const auxEntity = auxRef;
        if (!auxEntity.name || !auxEntity.fields) continue;
        if (auxEntity.local === true && auxEntity.seedMock !== true) continue;
        const auxFields = auxEntity.fields.filter(
          (f) => typeof f.name === "string" && f.name.length > 0
        );
        this.persistence.registerEntity({
          name: auxEntity.name,
          id: auxEntity.id,
          collection: auxEntity.collection,
          fields: auxFields,
          persistence: auxEntity.persistence,
          identity: auxEntity.identity
        });
        if (this.config.debug) {
          persistLog.debug("mock:seeded-auxiliary", {
            entity: auxEntity.name,
            count: this.persistence.count(auxEntity.name)
          });
        }
      }
    }
    if (this.config.debug) {
      registerLog.debug("register:orbital", {
        name: orbital.name,
        traitCount: (orbital.traits || []).length
      });
    }
  }
  /**
   * Register a single orbital (sync wrapper for backward compatibility)
   */
  registerOrbital(orbital) {
    this.registerOrbitalAsync(orbital).catch((err) => {
      registerLog.error("register:failed", {
        name: orbital.name,
        error: err instanceof Error ? err : String(err)
      });
    });
  }
  /**
   * Set up scheduled ticks for all traits
   */
  setupTicks() {
    this.cleanupTicks();
    for (const [orbitalName, registered] of this.orbitals) {
      for (const trait of registered.traits || []) {
        if (!trait.ticks || trait.ticks.length === 0) continue;
        for (const tick of trait.ticks) {
          this.registerTick(orbitalName, trait.name, tick, registered);
        }
      }
    }
    if (this.config.debug && this.tickBindings.length > 0) {
      registerLog.debug("register:ticks", { count: this.tickBindings.length });
    }
  }
  /**
   * Register a single tick
   */
  registerTick(orbitalName, traitName, tick, registered) {
    if (typeof tick.interval === "string" && isValidCronExpression(tick.interval)) {
      if (this.config.debug) {
        registerLog.debug("register:tick-cron", {
          orbital: orbitalName,
          trait: traitName,
          tick: tick.name,
          expression: tick.interval
        });
      }
      const handle2 = this.tickScheduler.addCron(tick.interval, () => {
        void this.executeTick(orbitalName, traitName, tick, registered);
      });
      this.tickBindings.push({ orbitalName, traitName, tick, handle: handle2 });
      return;
    }
    let intervalMs;
    if (typeof tick.interval === "number") {
      intervalMs = tick.interval;
    } else if (typeof tick.interval === "string") {
      intervalMs = parseDurationString(tick.interval);
    } else {
      intervalMs = 1e3;
    }
    if (this.config.debug) {
      registerLog.debug("register:tick", {
        orbital: orbitalName,
        trait: traitName,
        tick: tick.name,
        intervalMs
      });
    }
    const handle = this.tickScheduler.add(intervalMs, () => {
      void this.executeTick(orbitalName, traitName, tick, registered);
    });
    this.tickBindings.push({
      orbitalName,
      traitName,
      tick,
      handle
    });
  }
  /**
   * Execute a tick for all applicable entities
   */
  async executeTick(orbitalName, traitName, tick, registered) {
    if (tick.runsInBackground !== true && !this.isTraitMounted(orbitalName, traitName)) return;
    const entityType = registered.entity.name;
    const emittedEvents = [];
    const now = Date.now();
    try {
      if (tick.appliesTo && tick.appliesTo.length > 0) {
        const traitState = registered.manager.getState(traitName)?.currentState;
        if (traitState === void 0 || !tick.appliesTo.includes(traitState)) return;
      }
      const entities = await this.persistence.list(entityType);
      if (this.config.debug && entities.length > 0) {
        effectLog.debug("tick:processing", () => ({
          orbital: orbitalName,
          trait: traitName,
          tick: tick.name,
          entityCount: entities.length
        }));
      }
      for (const entity of entities) {
        if (tick.guard) {
          try {
            const ctx = createContextFromBindings({
              entity,
              payload: {},
              state: registered.manager.getState(traitName)?.currentState || "unknown",
              // A tick has no request user; only a dev host's ambient persona.
              user: this.config.defaultUser,
              now
            }, false, this.config.contextExtensions);
            const guardPasses = evaluateGuard(
              tick.guard,
              ctx
            );
            if (!guardPasses) {
              if (this.config.debug) {
                effectLog.debug("tick:guard-failed", () => ({
                  tick: tick.name,
                  entityId: typeof entity.id === "string" ? entity.id : void 0
                }));
              }
              continue;
            }
          } catch (error) {
            effectLog.error("tick:guard-error", {
              tick: tick.name,
              entityId: typeof entity.id === "string" ? entity.id : void 0,
              error: error instanceof Error ? error : String(error)
            });
            continue;
          }
        }
        if (tick.effects && tick.effects.length > 0) {
          const fetchedData = {};
          const clientEffects = [];
          const tickEffectResults = [];
          await this.executeEffects(
            registered,
            traitName,
            tick.effects,
            {},
            // No payload for ticks
            entity,
            entity.id,
            emittedEvents,
            fetchedData,
            clientEffects,
            tickEffectResults,
            this.config.defaultUser,
            void 0,
            void 0,
            void 0,
            void 0,
            now
          );
          if (this.config.debug) {
            effectLog.debug("tick:effects-executed", () => ({
              tick: tick.name,
              entityId: typeof entity.id === "string" ? entity.id : void 0
            }));
          }
        }
      }
      if (emittedEvents.length > 0) {
        this.enqueueEvent(async (queueHold) => {
          await this.relayEmittedEvents(orbitalName, emittedEvents, {
            includeSourceOrbital: true,
            visited: /* @__PURE__ */ new Set(),
            queueHold
          });
        }).catch((error) => {
          effectLog.error("tick:relay-error", {
            tick: tick.name,
            error: error.message
          });
        });
      }
    } catch (error) {
      effectLog.error("tick:execute-error", {
        tick: tick.name,
        error: error instanceof Error ? error : String(error)
      });
    }
  }
  /**
   * Clean up all active ticks
   */
  cleanupTicks() {
    for (const binding of this.tickBindings) {
      binding.handle.stop();
    }
    this.tickBindings = [];
  }
  /**
   * Unregister all orbitals and clean up
   */
  /** A client mounts a trait by dispatching its lifecycle event and releases it with `$UNMOUNT`. */
  recordMount(orbitalName, request, mounted) {
    const client = request.clientId ?? ANONYMOUS_CLIENT;
    const traits = request.mount !== void 0 ? request.mount.map((m) => m.trait) : request.targetTrait !== void 0 ? [request.targetTrait] : (request.traits ?? []).map((t) => t.trait);
    for (const traitName of traits) {
      const key = `${orbitalName}::${traitName}`;
      const clients = this.mountedBy.get(key) ?? /* @__PURE__ */ new Set();
      if (mounted) clients.add(client);
      else clients.delete(client);
      if (clients.size > 0) this.mountedBy.set(key, clients);
      else this.mountedBy.delete(key);
    }
  }
  /** Drop every mount a disconnected client held, so its traits' mount-scoped ticks pause. */
  releaseClient(clientId) {
    for (const [key, clients] of this.mountedBy) {
      clients.delete(clientId);
      if (clients.size === 0) this.mountedBy.delete(key);
    }
  }
  /** True while at least one client has `traitName` mounted. */
  isTraitMounted(orbitalName, traitName) {
    return (this.mountedBy.get(`${orbitalName}::${traitName}`)?.size ?? 0) > 0;
  }
  unregisterAll() {
    this.cleanupTicks();
    this.mountedBy.clear();
    this.orbitals.clear();
    this.eventBus.clear();
    this.messages = {};
    this.appLocales = [];
    if (this.persistence instanceof MockPersistenceAdapter) {
      this.persistence.clearAll();
    }
    if (this.osHandlers) {
      this.osHandlers.cleanup();
      this.osHandlers = null;
    }
    this.programHandlersPromise = null;
    if (this.registrationMutationDepth === 0) {
      this.registrationEpoch = (this.registrationEpoch ?? Promise.resolve()).then(() => void 0, () => void 0);
    }
  }
  /**
   * Bracket a multi-step app mutation (the classic swap is
   * `unregisterAll()` → resolve next schema → `register()`) as ONE
   * registration epoch. `register()` alone chains only its own body onto
   * the epoch, so between `unregisterAll()` and the `register()` call the
   * runtime's map is empty while the epoch marker has already resolved —
   * any `processOrbitalEvent` that lands in that gap (the resolve of a big
   * organism takes real time) 404s `Orbital not found` even though the
   * requested orbital is milliseconds away from landing. Inside this
   * bracket the epoch stays pending for the WHOLE callback, so mid-swap
   * dispatches await the completed swap and then resolve against the new
   * app. A failed callback still releases the epoch (chained on both
   * branches), so a failed swap falls through to the honest 404 instead of
   * wedging the queue. Callbacks SERIALIZE: each bracket awaits the previous
   * epoch before running `fn`, so two hosts swapping at once (a catalog
   * select racing a preview remount's `/register`) cannot interleave their
   * unregister/register sequences into a mixed registry. A bracket opened
   * from INSIDE another bracket's callback runs `fn` directly — detected
   * via the AsyncLocalStorage context below (a depth counter alone can't
   * tell a true nest from a concurrent caller) — because the outer
   * callback's awaits already serialize it and queueing on the outer gate
   * would deadlock. Deadlock-free otherwise: `doRegister` never dispatches
   * events, so awaiting dispatches never block registration progress.
   */
  /**
   * Reentrancy context for `withRegistrationMutation`. A depth counter alone
   * cannot distinguish "called from inside a bracket callback" (must run
   * directly — queueing on the outer gate would deadlock) from "called
   * concurrently while another bracket is in flight" (must queue — running
   * directly would interleave two swaps). The ALS marks the callback's async
   * context, which separates the two exactly.
   */
  bracketAls = null;
  ensureBracketAls() {
    this.bracketAls ??= import('async_hooks').then(
      ({ AsyncLocalStorage }) => new AsyncLocalStorage()
    );
    return this.bracketAls;
  }
  async withRegistrationMutation(fn) {
    const previous = this.registrationEpoch ?? Promise.resolve();
    let release = () => void 0;
    const gate = new Promise((resolve) => {
      release = resolve;
    });
    this.registrationEpoch = previous.then(() => gate, () => gate);
    this.registrationMutationDepth++;
    try {
      const als = await this.ensureBracketAls();
      if (als.getStore() !== void 0) {
        release();
        return await fn();
      }
      await previous.catch(() => void 0);
      const nestingToken = /* @__PURE__ */ Symbol("withRegistrationMutation");
      return await als.run(nestingToken, fn);
    } finally {
      this.registrationMutationDepth--;
      release();
    }
  }
  /**
   * Reset the mock persistence store to a clean-slate re-seed without
   * unregistering orbitals. Exposed for verifier tools that want to
   * start each test with deterministic seeded rows, not the residue of
   * the previous walk's persist-creates. No-op when the persistence
   * layer is not MockPersistenceAdapter.
   */
  resetMockPersistence() {
    if (!(this.persistence instanceof MockPersistenceAdapter)) return;
    busLog.debug("mock:reset:enter", {
      orbitalCount: this.orbitals.size,
      timestamp: (/* @__PURE__ */ new Date()).toISOString()
    });
    this.persistence.clearAll();
    if (this.resolvedSchema) this.seedIdentityEntity(this.resolvedSchema);
    for (const registered of this.orbitals.values()) {
      const entity = registered.entity;
      if (entity?.name && entity.fields) {
        const fields = entity.fields.filter(
          (f) => typeof f.name === "string" && f.name.length > 0
        );
        this.persistence.registerEntity({
          name: entity.name,
          id: entity.id,
          collection: entity.collection,
          fields,
          persistence: entity.persistence,
          identity: entity.identity
        });
      }
      for (const auxRef of registered.schema.auxiliaryEntities ?? []) {
        if (typeof auxRef === "string" || isEntityCall(auxRef)) continue;
        if (!auxRef.name || !auxRef.fields) continue;
        const auxFields = auxRef.fields.filter(
          (f) => typeof f.name === "string" && f.name.length > 0
        );
        this.persistence.registerEntity({
          name: auxRef.name,
          id: auxRef.id,
          collection: auxRef.collection,
          fields: auxFields,
          persistence: auxRef.persistence,
          identity: auxRef.identity
        });
      }
    }
  }
  /**
   * Return every registered orbital's circuit to boot: trait states to their
   * initial states, frames and row cache emptied, and a fresh mount (no trait
   * awaiting INIT, nothing held). The hermetic-reset twin of
   * `resetMockPersistence`: a reset walk must not inherit the previous walk's
   * circuit any more than its rows.
   */
  resetCircuitState() {
    for (const registered of this.orbitals.values()) {
      registered.manager.resetAll();
      registered.traitFieldStates.clear();
      registered.entityData.clear();
      registered.mount = new MountLifecycle();
    }
  }
  /**
   * Wire a live-broadcast sink. Called only from a `persist` effect's
   * `emit:{success}` envelope firing site (batch and single-op) — see
   * `docs/Almadar_Live_Push.md`. The hosting server (e.g. the playground's
   * `/api/events` SSE endpoint) uses this to fan persist mutations out to
   * every OTHER connected client; a fresh call replaces the previous sink.
   */
  setLiveBroadcastSink(sink) {
    this.liveBroadcastSink = sink;
  }
  /** Wire where a host-run dispatch's result goes (see `HostDispatchItem`); a fresh call replaces the previous sink. */
  setHostDispatchSink(sink) {
    this.hostDispatchSink = sink;
  }
  /** Run a follow-on step's call outcome as its own dispatch, and hand its result to the host-dispatch sink. */
  runDeferredOutcome(orbitalName, request) {
    void this.processOrbitalEvent(orbitalName, request).then(
      (response) => this.hostDispatchSink?.({ orbitalName, request, response, ...request.clientId !== void 0 ? { originClientId: request.clientId } : {} }),
      (error) => effectLog.error("deferred-outcome:failed", { orbitalName, event: request.event, trait: request.targetTrait, error: error.message })
    );
  }
  /**
   * Switch the viewer a dev host presents the app as — see
   * `OrbitalServerRuntimeConfig.defaultUser`. Pass `undefined` for an
   * unauthenticated viewer. Takes effect on the next event; an authenticated
   * request still overrides it.
   *
   * Mock mode seeded owner columns EAGERLY at construction, stamped with
   * whichever id was `defaultUser` then (see `MockPersistenceAdapter.seed`).
   * Switching to a different id here would otherwise leave those columns
   * pointing at the old viewer forever, so an ownership-scoped view for the
   * new one stays empty — `restampOwner` re-points the already-stamped cells
   * instead of re-seeding.
   */
  setDefaultUser(user) {
    const previousId = this.config.defaultUser?.id;
    this.config.defaultUser = user;
    this.viewerNamedByHost = true;
    if (this.persistence instanceof MockPersistenceAdapter && user?.id !== void 0 && user.id !== previousId) {
      this.restampOwnerGated(user);
    }
  }
  /** Whether the host named the viewer (constructor or `setDefaultUser`). If not, the viewer is
   *  the app's first declared persona, resolved as soon as its roster is seeded. */
  viewerNamedByHost = false;
  /** setDefaultUser can land before register() finishes resolving the schema
   *  (a dev host pins a persona right after boot). An ungated restamp there
   *  would hand a read-only persona authorship of drafts, so the restamp is
   *  DEFERRED until the schema exists — register()/registerSync() flush it. */
  pendingOwnerRestamp = false;
  /**
   * Re-point the mock seeder's owner stamps at `user`. The adapter's
   * installed owner gate (see installOwnerGate) decides per row whether the
   * persona could have CREATED it — a reader keeps seeing the store's
   * published face instead of inheriting draft ownership from the demo
   * stamper. Deferred when the schema (and so the gate) isn't resolved yet.
   */
  restampOwnerGated(user) {
    if (!(this.persistence instanceof MockPersistenceAdapter)) return;
    if (!this.resolvedSchema) {
      this.pendingOwnerRestamp = true;
      return;
    }
    this.persistence.restampOwner(user.id);
  }
  /**
   * Install the schema's `@create` directives as the mock seeder's owner
   * gate — the authority on who may author a row, unless `@read` scopes the
   * owner column by ownership (then its owner-readers own the rows). Consulted by BOTH
   * `restampOwner` (persona switches) and `seed()` (a client connect
   * re-registers, and the deterministic reseed re-stamps with the current
   * ownerId — gating only the switch path would leak right there). Reads the
   * CURRENT default user at evaluation time so one installation covers every
   * later switch. Installed before the first seed, so the initial rows are
   * gated exactly as every reseed is.
   */
  installOwnerGate(schema) {
    if (!(this.persistence instanceof MockPersistenceAdapter)) return;
    installPolicyOwnerGates(this.persistence, schema, () => this.config.defaultUser);
  }
  /** Flush a persona switch that arrived before the schema was resolved. */
  flushPendingOwnerRestamp() {
    if (this.pendingOwnerRestamp) {
      this.pendingOwnerRestamp = false;
      const user = this.config.defaultUser;
      if (user?.id !== void 0) this.restampOwnerGated(user);
    }
  }
  /** The viewer a dev host is currently presenting the app as. */
  getDefaultUser() {
    return this.config.defaultUser;
  }
  /**
   * Queue a tick-stamped dispatch for coalesced snapshot relay (T6). Newest
   * per (originClientId, orbital, event) wins between flushes — the standard
   * netcode position-broadcast discipline.
   */
  queueTickRelay(orbitalName, request) {
    const key = `${request.clientId ?? ""}:${orbitalName}:${request.event}`;
    this.tickRelayPending.set(key, {
      event: request.event,
      payload: request.payload,
      source: { orbital: orbitalName, trait: request.sourceTrait, tick: request.tick },
      originClientId: request.clientId,
      target: "peers"
    });
    if (this.tickRelayTimer === null) {
      const timer = setInterval(() => this.flushTickRelay(), this.config.tickRelayIntervalMs ?? 50);
      if (typeof timer === "object" && typeof timer.unref === "function") timer.unref();
      this.tickRelayTimer = timer;
    }
  }
  flushTickRelay() {
    if (this.tickRelayPending.size === 0) {
      if (this.tickRelayTimer !== null) {
        clearInterval(this.tickRelayTimer);
        this.tickRelayTimer = null;
      }
      return;
    }
    const items = Array.from(this.tickRelayPending.values());
    this.tickRelayPending.clear();
    for (const item of items) this.liveBroadcastSink?.(item);
  }
  /**
   * The registered app's declared persona roster: the live rows of its
   * `[identity]` entity, mapped onto viewers (`Almadar_LOLO_Identity.md` §4.3).
   * Live-store rows rather than a re-derivation, so the roster carries the ids
   * ownership scoping actually compares `@user.id` against. Empty when no
   * schema is registered or the app declares no `[identity]` entity — there is
   * no global fallback roster by design.
   */
  /**
   * `@user` for an HTTP request: the user the host's auth middleware verified (`req.authUser`),
   * resolved against the program's `[identity]` entity, or the anonymous viewer; a tool runtime's
   * declared `principal` instead. A request never borrows `config.defaultUser`.
   */
  async viewerOf(verified) {
    if (this.config.principal !== void 0) return this.config.principal;
    const source = this.resolvedSchema ? identitySourceOf(this.resolvedSchema) : { kind: "none" };
    return resolveViewer(verified ?? null, source, this.persistence);
  }
  async getIdentityRoster() {
    const schema = this.resolvedSchema;
    if (!schema) return [];
    const entityName = identityEntityName(schema);
    if (!entityName) return [];
    const rows = await this.persistence.list(entityName);
    return rows.map((row) => personaFromIdentityRow(row)).filter((p) => p !== void 0);
  }
  // ==========================================================================
  // Event Processing
  // ==========================================================================
  /**
   * `traitFieldStates` key for one trait: its own name, or — when its linked
   * entity is `[shared]` — `$shared::<entityName>` so every trait bound to
   * that entity reads/writes ONE frame. The server twin of the client hook's
   * `sharedKeyByTraitName` (useTraitStateMachine): without it, a sibling
   * trait's effect interpolation (a fetch filter reading
   * `@entity.activeChannel`) never saw the writer's `(set …)`.
   */
  sharedFieldKey(registered, traitName) {
    const trait = registered.traits.find((t) => t.name === traitName);
    const linked = trait?.linkedEntity ?? registered.entity.name;
    return this.isSharedEntity(registered, linked) ? `$shared::${linked}` : traitName;
  }
  /** Resolve an entity name to its declared `shared` flag: the orbital's own
   *  entity, its auxiliary entities, then other registered orbitals' primary
   *  entities (cross-orbital binds like a membership rail in the chat page). */
  isSharedEntity(registered, entityName) {
    if (registered.entity.name === entityName) return registered.entity.shared === true;
    for (const aux of registered.schema.auxiliaryEntities ?? []) {
      if (typeof aux === "object" && !isEntityCall(aux) && aux.name === entityName) {
        return aux.shared === true;
      }
    }
    for (const other of this.orbitals.values()) {
      if (other.entity.name === entityName) return other.entity.shared === true;
    }
    return false;
  }
  /** Resolve an entity by name the same way `isSharedEntity` does: the
   *  orbital's own entity, its auxiliary entities, then other registered
   *  orbitals' primary entities (cross-orbital binds like a membership rail
   *  in the chat page). The one entity-resolution walk shared by every
   *  field-level lookup (`intrinsicFieldNames`, `entityFieldsFor`) so a new
   *  lookup never grows a second copy of this walk. */
  resolveEntityByName(registered, entityName) {
    if (registered.entity.name === entityName) return registered.entity;
    for (const aux of registered.schema.auxiliaryEntities ?? []) {
      if (typeof aux === "object" && !isEntityCall(aux) && aux.name === entityName) {
        return aux;
      }
    }
    for (const other of this.orbitals.values()) {
      if (other.entity.name === entityName) return other.entity;
    }
    return void 0;
  }
  /** Names of an entity's `@intrinsic` fields — trait-owned view state
   *  (`ChatMessage.activeChannel`, `ChannelMember.pendingChannelId`, …) that
   *  is NEVER a persisted column. */
  intrinsicFieldNames(registered, entityName) {
    return this.entityFieldsFor(registered, entityName).filter((field) => field.intrinsic === true && typeof field.name === "string").map((field) => field.name);
  }
  /** Full declared field list for an entity — the schema source `persist
   *  create`'s required-column check reads (`EffectExecutor.
   *  resolveEntityFields`), via the same `resolveEntityByName` walk
   *  `intrinsicFieldNames` uses. */
  entityFieldsFor(registered, entityName) {
    return this.resolveEntityByName(registered, entityName)?.fields ?? [];
  }
  /**
   * Process an event for an orbital — the stateful host's thin shell over
   * the unified `evaluateOrbitalEvent` composition (design:
   * docs/Almadar_Runtime_Stateless_Stateful_PLAN.md §4.1). This method owns
   * ONLY session concerns: orbital lookup, viewer normalization, the lazy
   * OS/substrate handler wiring, the per-request relay mask
   * (G-RUNTIME-031 — the client's mounted set as DATA, replacing the
   * deleted blanket `originClientId` skip), the tick snapshot relay, and
   * the cross-orbital listens relay (a listener registered under a
   * DIFFERENT orbital than the emitter — the per-orbital composition's
   * in-band fan-out covers the rest). Target resolution, payload
   * validation, the cascade, the fan-out, structured rejections, and
   * response assembly are the composition's, identical to the stateless
   * path.
   *
   * Serialization: the whole evaluation runs on ONE runtime-wide async
   * queue. The composition commits manager state directly (no per-trait
   * actor queue), so concurrent requests against the long-lived session
   * manager would otherwise both evaluate from the same held state and
   * lose a transition. Coarser than the old per-(trait, entityId) actor
   * queues — a deliberate W4 tradeoff, recorded in the plan doc.
   *
   * @param onPush - Optional incremental-push callback. Called immediately
   * when an event is emitted or a client effect fires, before the promise
   * resolves. Used by the SSE streaming path to write items to the
   * response as they arrive rather than buffering and flushing at the end.
   */
  /**
   * Read an orbital by name, awaiting an in-flight registration mutation
   * first when the lookup misses (G-RUNTIME-038): mid-swap lookups 404ed
   * against the emptied map even though the requested orbital was
   * milliseconds from landing. A failed mutation falls through to the miss.
   */
  async resolveRegisteredOrbital(orbitalName) {
    let registered = this.orbitals.get(orbitalName);
    if (!registered && this.registrationEpoch !== null) {
      await this.registrationEpoch.catch(() => void 0);
      registered = this.orbitals.get(orbitalName);
    }
    return registered;
  }
  /** The program's declared external inputs: what an outside client may send which trait. */
  listExternalInputs() {
    return this.resolvedSchema ? externalInputsOf(this.resolvedSchema) : [];
  }
  /**
   * The outside-client input channel: an API caller, an agent acting as the
   * user, an MCP client. Only a declared external input of the target trait is
   * dispatched — through `processOrbitalEvent`, so guards, entity policies and
   * cascades apply exactly as for a click. Anything else is refused with
   * `not-an-external-input`. The app's own UI keeps using `processOrbitalEvent`.
   */
  /**
   * The running app lent to one `call-service` provider as the caller: the
   * declared inputs, the input channel as `user`, and the rows `user` may read
   * (the entity's `@read` policy, the same rule a `fetch` effect applies).
   */
  servicePorts(caller, user, message = () => {
  }) {
    return {
      caller,
      message,
      inputs: () => this.listExternalInputs(),
      readableEntities: () => this.resolvedSchema ? readableEntitiesOf(this.resolvedSchema) : [],
      dispatchInput: (orbital, request) => this.dispatchExternalInput(orbital, { ...request, ...user !== void 0 ? { user } : {} }),
      read: async (entity) => {
        const readPolicy = this.resolvedSchema ? entityAccessPolicies(this.resolvedSchema, entity)?.read : void 0;
        return applyRowAccess(await this.persistence.list(entity), readPolicy, void 0, { user });
      }
    };
  }
  async dispatchExternalInput(orbitalName, request, onPush) {
    return dispatchDeclaredInput(this.resolvedSchema, orbitalName, request, (orbital, req) => this.processOrbitalEvent(orbital, req, onPush));
  }
  async processOrbitalEvent(orbitalName, request, onPush) {
    try {
      await this.ensureOsHandlers();
      await this.ensureProgramHandlers();
      const registered = await this.resolveRegisteredOrbital(orbitalName);
      if (!registered) {
        return {
          success: false,
          transitioned: false,
          states: {},
          emittedEvents: [],
          error: `Orbital not found: ${orbitalName}`
        };
      }
      if (request.event === UNMOUNT_EVENT) {
        this.recordMount(orbitalName, request, false);
        return { success: true, transitioned: false, states: {}, emittedEvents: [] };
      }
      if (request.mount !== void 0 || LIFECYCLE_EVENTS.includes(request.event)) {
        this.recordMount(orbitalName, request, true);
      }
      const response = await this.enqueueEvent(
        (queueHold) => this.evaluateForOrbital(registered, request, onPush, queueHold)
      );
      if (request.tick !== void 0 && this.liveBroadcastSink !== null) {
        this.queueTickRelay(orbitalName, request);
      }
      return response;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      xOrbitalLog.error("processOrbitalEvent:error", {
        orbital: orbitalName,
        event: request.event,
        entityId: request.entityId,
        error: message
      });
      return {
        success: false,
        transitioned: false,
        states: {},
        emittedEvents: [],
        error: message
      };
    }
  }
  /**
   * The enqueued critical section behind `processOrbitalEvent`: evaluate
   * one request through the composition, then relay its emits to
   * cross-orbital listeners. MUST only run holding the event queue.
   */
  async evaluateForOrbital(registered, request, onPush, queueHold) {
    const orbitalName = registered.schema.name;
    const payloadRow = request.payload?.["row"];
    const payloadRowAsPayload = payloadRow !== null && typeof payloadRow === "object" && !Array.isArray(payloadRow) ? payloadRow : void 0;
    const payloadRowId = payloadRowAsPayload?.["id"];
    renderLog.debug("processOrbitalEvent:enter", {
      orbital: orbitalName,
      event: request.event,
      hasPayloadRow: payloadRowAsPayload !== void 0,
      payloadRowId: typeof payloadRowId === "string" || typeof payloadRowId === "number" ? payloadRowId : void 0,
      entityId: request.entityId
    });
    busLog.debug("bus:incoming", () => ({
      orbital: orbitalName,
      event: request.event,
      payload: JSON.stringify(request.payload ?? null),
      entityId: request.entityId,
      traitStates: JSON.stringify(
        Array.from(registered.manager.getAllStates().entries()).map(([traitName, state]) => ({
          traitName,
          currentState: state.currentState
        }))
      )
    }));
    xOrbitalLog.info("processOrbitalEvent:enter", () => ({
      orbital: orbitalName,
      event: request.event,
      traitsInOrbital: registered.traits.map((t) => t.name).join(","),
      payloadActiveTraits: JSON.stringify(
        request.payload?.["_activeTraits"] ?? null
      )
    }));
    const viewer = normalizeUserContext(request.user) ?? this.config.defaultUser;
    if (request.locale !== void 0 && this.appLocales.length > 0 && !this.appLocales.includes(request.locale)) {
      throw new Error(`locale "${request.locale}" is not one of the program's declared locales [${this.appLocales.join(", ")}]`);
    }
    const isDelegatedLeg = (request.targetTrait !== void 0 || request.mount !== void 0) && (request.traits !== void 0 || request.entityByTrait !== void 0);
    let serverRequest = request;
    if (!isDelegatedLeg && request.targetTrait === void 0) {
      const sidecarTarget = request.payload?.["_targetTrait"];
      if (sidecarTarget !== void 0 || request.traits !== void 0 || request.entityByTrait !== void 0) {
        serverRequest = { ...request, ...sidecarTarget !== void 0 ? { targetTrait: sidecarTarget } : {} };
        delete serverRequest.traits;
        delete serverRequest.entityByTrait;
      }
    }
    const response = await evaluateOrbitalEvent(
      this.makeEvaluateDeps(registered, { viewer, onPush, originClientId: request.clientId, queueHold, ...request.locale !== void 0 ? { locale: request.locale } : {} }),
      serverRequest
    );
    await this.relayEmittedEvents(orbitalName, response.emittedEvents, {
      includeSourceOrbital: false,
      visited: /* @__PURE__ */ new Set(),
      queueHold,
      into: response,
      ...viewer !== void 0 ? { viewer } : {}
    });
    return response;
  }
  /**
   * Build the composition deps for one registered orbital — the stateful
   * session shape: long-lived manager + frames, the server effect stage
   * (relation trio + live broadcast), and the per-request viewer/push/
   * origin context. The stage runner also re-runs JSX-hoisted
   * callsite-capture children under each step's payload (see
   * `rerenderCallsiteCaptureChildren`).
   */
  makeEvaluateDeps(registered, context) {
    const runEffects = async (traitName, args) => {
      const originClientId = args.originClientId ?? context.originClientId;
      await this.executeEffects(
        registered,
        traitName,
        args.effects,
        args.payload,
        args.entityData,
        args.entityId,
        args.emittedEvents,
        args.fetchedData,
        args.clientEffects,
        args.effectResults,
        context.viewer,
        args.clientEffectsByTrait,
        context.onPush,
        originClientId,
        void 0,
        args.now,
        args.dispatch,
        args.firing,
        context.queueHold,
        args.locale,
        args.followOn
      );
      await this.rerenderCallsiteCaptureChildren(
        registered,
        traitName,
        args.payload ?? {},
        args.entityData,
        args.entityId,
        args.emittedEvents,
        args.fetchedData,
        args.clientEffects,
        args.effectResults,
        context.viewer,
        args.clientEffectsByTrait,
        context.onPush,
        originClientId,
        args.now,
        /* @__PURE__ */ new Set(),
        false,
        context.queueHold,
        args.locale,
        this.composedByEffects(registered, traitName, args.effects)
      );
    };
    return {
      traitIndex: this.traitIndexFor(registered),
      manager: registered.manager,
      persistence: this.persistence,
      frames: registered.traitFieldStates,
      mount: registered.mount,
      runEffects,
      // This host holds every trait's state and runs every listener itself:
      // an explicit empty mask, so the composition's request-shape mask (for
      // a host that holds no state) never silences a target's own listens.
      relayMask: /* @__PURE__ */ new Set(),
      ...context.viewer !== void 0 ? { user: context.viewer } : {},
      ...context.locale !== void 0 ? { locale: context.locale } : {},
      ...context.originClientId !== void 0 ? { originClientId: context.originClientId } : {},
      ...this.config.contextExtensions !== void 0 ? { contextExtensions: this.config.contextExtensions } : {},
      ...this.config.debug !== void 0 ? { debug: this.config.debug } : {},
      logContext: { orbitalName: registered.schema.name }
    };
  }
  /**
   * The orbital's evaluation index, built lazily on first use (by then
   * every orbital is registered, so cross-orbital `linkedEntity`
   * resolution sees the full registry) and cached on the registration.
   */
  traitIndexFor(registered) {
    if (registered.traitIndex === void 0) {
      registered.traitIndex = buildTraitIndexForOrbital(
        registered.schema,
        [...this.orbitals.values()].filter((other) => other !== registered).map((other) => other.schema)
      );
    }
    return registered.traitIndex;
  }
  /**
   * The runtime-wide event queue — ONE async serialization point for every
   * composition run (requests, cross-orbital relays, external emits, tick
   * relays). The composition commits manager state directly rather than
   * through the manager's per-(trait, entityId) actor queues, so without
   * this, two concurrent requests against the session manager could both
   * evaluate from the same held state and lose a transition. `run` sees
   * the caller's result (or throw); the queue itself never wedges on a
   * failure.
   */
  eventQueue = Promise.resolve();
  /** This app's in-flight keyed `call-service`s, shared by every transition it runs (`cancel-call`). */
  inFlightCalls = new InFlightCalls();
  /** Wait for the queue, then hold it until the returned release is called. FIFO. */
  acquireEventQueue() {
    const previous = this.eventQueue;
    let release = () => void 0;
    const held = new Promise((resolve) => {
      release = resolve;
    });
    this.eventQueue = previous.then(() => held);
    return previous.then(() => release);
  }
  /**
   * Hold the queue for `run`, handing it the hold so the work it reaches can
   * release it (`outsideEventQueue`). The hold is passed explicitly rather than
   * carried in async context: this runtime also runs in the browser (the orb
   * website), which has no AsyncLocalStorage.
   */
  async enqueueEvent(run) {
    const hold = { held: true, release: await this.acquireEventQueue() };
    try {
      return await run(hold);
    } finally {
      if (hold.held) hold.release();
    }
  }
  /**
   * Run `work` with this context's queue hold released, then re-acquire it (in
   * FIFO order behind whatever queued meanwhile). Dispatches that land while
   * released run as their own dispatches; their commits and the held run's
   * later commit follow server-arrival last-write-wins. Outside a hold, `work`
   * just runs.
   */
  async outsideEventQueue(hold, work) {
    if (hold === void 0 || !hold.held) return work();
    hold.held = false;
    hold.release();
    try {
      return await work();
    } finally {
      hold.release = await this.acquireEventQueue();
      hold.held = true;
    }
  }
  /**
   * Cross-orbital listens relay — the host half of the circuit the
   * per-orbital composition cannot see: a listener registered under a
   * DIFFERENT orbital than the emitter's. Each match runs the listener's
   * `triggers` event through the listener's own orbital composition
   * (targeted delivery — the same shape the deleted bus fan-out posted,
   * minus its blanket `originClientId` skip: G-RUNTIME-031). Recursion is
   * bounded by a per-entry visited set (distinct
   * orbital/trait/triggers/from keys, mirroring the composition's own
   * cascade visited-set) plus a hop cap. MUST only run holding the event
   * queue.
   *
   * @param sourceOrbital The orbital whose composition produced `emits`,
   *   or undefined for external producers (os/substrate handlers). The
   *   source orbital is skipped unless `includeSourceOrbital` — its own
   *   listeners already ran in-band (composition emits), or the caller
   *   explicitly wants them (ticks, which never enter the composition).
   */
  async relayEmittedEvents(sourceOrbital, emits, opts) {
    const queue = [...emits];
    let hops = 0;
    while (queue.length > 0) {
      const emitted = queue.shift();
      if (emitted === void 0) break;
      const producedIn = emitted.source?.orbital ?? sourceOrbital;
      for (const [orbitalName, registered] of this.orbitals) {
        if (orbitalName === producedIn && !opts.includeSourceOrbital) continue;
        const delivery = deliveryOf(emitted);
        const traitIndex = this.traitIndexFor(registered);
        for (const target of collectListenerTargets(
          traitIndex,
          emitted.source,
          emitted.event,
          emitted.payload,
          void 0,
          {
            guardBindings: freshDeliveryGuardBindings(delivery, (listener) => {
              const entry = traitIndex.byName.get(listener);
              return registered.manager.getState(listener)?.currentState ?? (entry !== void 0 ? findInitialState(entry.traitDef) : "");
            })
          }
        )) {
          const from = registered.manager.getState(target.listenerTrait, target.entityId)?.currentState ?? findInitialState(target.entry.traitDef);
          const frame = await readTraitFrame(this.persistence, registered.traitFieldStates, target.entry, target.entityId);
          const key = `${orbitalName}\0${dispatchVisitKey(target.listenerTrait, target.triggers, from, target.payload, frame)}`;
          if (opts.visited.has(key)) continue;
          if (hops >= CROSS_ORBITAL_RELAY_CAP) {
            xOrbitalLog.warn("relay:truncated", { event: emitted.event, hops });
            return;
          }
          opts.visited.add(key);
          hops += 1;
          try {
            const response = await evaluateOrbitalEvent(
              {
                ...this.makeEvaluateDeps(registered, {
                  viewer: opts.viewer ?? this.config.defaultUser,
                  ...opts.queueHold !== void 0 ? { queueHold: opts.queueHold } : {},
                  ...emitted.source?.originClientId !== void 0 ? { originClientId: emitted.source.originClientId } : {}
                }),
                seedDelivery: delivery
              },
              {
                event: target.triggers,
                ...target.triggersId !== void 0 ? { eventId: target.triggersId } : {},
                ...target.payload !== void 0 ? { payload: target.payload } : {},
                ...target.entityId !== void 0 ? { entityId: target.entityId } : {},
                targetTrait: target.listenerTrait
              }
            );
            queue.push(...response.emittedEvents);
            if (opts.into !== void 0) foldRelayHop(opts.into, response);
          } catch (error) {
            xOrbitalLog.error("relay:error", {
              orbital: orbitalName,
              trait: target.listenerTrait,
              event: target.triggers,
              error: error instanceof Error ? error.message : String(error)
            });
          }
        }
      }
    }
  }
  /**
   * Entry point for events produced OUTSIDE any orbital's composition —
   * the OS effect handlers (fs/net/child_process watchers) and the agent
   * substrate handlers (the deleted bus's last producers). Fans the event
   * out to every registered orbital's matching listeners. Runs on the
   * event queue; errors are logged, never thrown at the producer.
   */
  dispatchExternalEvent(event, payload) {
    this.enqueueEvent(async (queueHold) => {
      await this.relayEmittedEvents(
        void 0,
        [{ event, ...payload !== void 0 ? { payload } : {} }],
        { includeSourceOrbital: true, visited: /* @__PURE__ */ new Set(), queueHold }
      );
    }).catch((error) => {
      xOrbitalLog.error("dispatchExternalEvent:error", {
        event,
        error: error.message
      });
    });
  }
  /**
   * Execute effects from a transition
   */
  async executeEffects(registered, traitName, effects, payload, entityData, entityId, emittedEvents, fetchedData, clientEffects, effectResults, user, clientEffectsByTrait, onPush, originClientId, callsitePayload, now, dispatch, firing, queueHold, locale, followOn) {
    if (callsitePayload !== void 0) this.composedCallsitePayloads.set(traitName, callsitePayload);
    const capture = callsitePayload ?? this.composedCallsitePayloads.get(traitName);
    const framesBefore = clientEffectsByTrait?.length ?? 0;
    const sigilPages = appNavItems([...this.orbitals.values()].map((reg) => reg.schema));
    const sigilTheme = sigilThemeKey(registered.schema.theme, this.appThemeKey);
    await runServerEffectStage(
      {
        persistence: this.persistence,
        frames: registered.traitFieldStates,
        frameKeyFor: (t) => this.sharedFieldKey(registered, t),
        orbitalName: registered.schema.name,
        orbitalId: registered.schema.id,
        irTraits: registered.traits,
        entity: registered.entity,
        configByTrait: registered.configByTrait,
        resolvedTraitConfigs: this.resolvedTraitConfigs,
        resolvedSchema: this.resolvedSchema,
        registeredOrbitals: this.orbitals.values(),
        getTraitState: (t) => registered.manager.getState(t),
        intrinsicFieldNames: (type) => this.intrinsicFieldNames(registered, type),
        entityFieldsFor: (type) => this.entityFieldsFor(registered, type),
        validateRelationCardinality: (type, data) => this.validateRelationCardinality(type, data),
        enforceOnDeleteRules: (type, id) => this.enforceOnDeleteRules(type, id),
        populateRelations: (entities, type, include) => this.populateRelations(registered, entities, type, include),
        sigilPages,
        sigilTheme,
        extraEffectHandlers: this.config.effectHandlers,
        outsideEventQueue: (work) => this.outsideEventQueue(queueHold, work),
        inFlightCalls: this.inFlightCalls,
        servicePorts: (caller, user2, message) => this.servicePorts(caller, user2, message),
        deliverEmit: (event, eventPayload, stamp, fromPersistSuccess) => {
          this.eventBus.emit(event, eventPayload, stamp, eventRouteKey(event, stamp.eventId));
          if (fromPersistSuccess) {
            this.liveBroadcastSink?.({ event, payload: eventPayload, source: stamp, originClientId, target: "peers" });
          }
        },
        deliverLive: (event, eventPayload, stamp) => {
          this.eventBus.emit(event, eventPayload, stamp, eventRouteKey(event, stamp.eventId));
          this.liveBroadcastSink?.({ event, payload: eventPayload, source: stamp, originClientId, target: "origin" });
        },
        liveBroadcastSinkWired: this.liveBroadcastSink !== null,
        // Only a host that can deliver a host-run dispatch to the tab defers; otherwise the call holds the request.
        ...this.hostDispatchSink !== null ? { deferCallOutcome: (call) => this.runDeferredOutcome(registered.schema.name, {
          event: call.event,
          ...call.payload !== void 0 ? { payload: call.payload } : {},
          targetTrait: call.traitName,
          ...call.entityId !== void 0 ? { entityId: call.entityId } : {},
          ...call.originClientId !== void 0 ? { clientId: call.originClientId } : {},
          ...user !== void 0 ? { user } : {},
          ...locale !== void 0 ? { locale } : {}
        }) } : {},
        debug: this.config.debug,
        mockMode: this.config.mode === "mock",
        contextExtensions: this.config.contextExtensions,
        messages: this.messages,
        ...this.appLocales[0] !== void 0 ? { defaultLocale: this.appLocales[0] } : {}
      },
      { traitName, effects, payload, entityData, entityId, emittedEvents, fetchedData, clientEffects, effectResults, user, clientEffectsByTrait, onPush, originClientId, callsitePayload: capture, ...locale !== void 0 ? { locale } : {}, ...now !== void 0 ? { now } : {}, ...dispatch !== void 0 ? { dispatch } : {}, ...firing !== void 0 ? { firing } : {}, ...followOn === true ? { followOn } : {} }
    );
    if (capture !== void 0 && clientEffectsByTrait !== void 0) {
      for (const frame of clientEffectsByTrait.slice(framesBefore)) {
        if (frame.traitName === traitName) frame.callsitePayload = capture;
      }
    }
  }
  /**
   * Re-run a JSX-hoisted inline child trait's (`@trait.X`) lifecycle
   * transition under `callsitePayload` — the payload of the transition that
   * just composed it — so its `@callsitePayload.<field>` captures reflect
   * the composing event instead of staying frozen at whatever the child
   * captured at its own mount-time INIT (a child renders once at mount and
   * never again on its own).
   *
   * `this.callsiteCaptureChildrenByTrait` (built at `register()` via
   * `@almadar/core`'s `collectCallsiteCaptureChildren`) gives `traitName`'s
   * DIRECT children that need this — either because the child itself
   * captures, or because it is a pass-through to a capturing descendant.
   * The child's lifecycle event (INIT/LOAD/$MOUNT) is re-dispatched
   * TARGETED at just that trait, from its CURRENT state, only when that
   * arm stays in the state (`repaintLifecycleEvent` — a repaint never moves
   * the child; otherwise only its descendants are visited), then its effects run through the SAME `executeEffects` used
   * everywhere else, with `payload: {}` (a lifecycle event carries none)
   * and `callsitePayload` set so `@callsitePayload.*` resolves — pushing
   * into the SAME `clientEffects`/`clientEffectsByTrait`/`effectResults`
   * so the child's refreshed frame reaches the sidecar under its own trait
   * name. Recurses into the child's own entry in the same map (still under
   * the SAME `callsitePayload` — the capture resolves up the embed chain to
   * the nearest transition that actually has one) for grandchildren;
   * `visited` guards against a malformed embed graph cycling on itself.
   * Never goes through `processOrbitalEvent` (that would be re-entrant) —
   * calls this internal executor directly, exactly like every other
   * transition's effects.
   */
  /** The callsite-capture children some fired effects of `traitName` compose (core `traitsEmbeddedByEffects`). */
  composedByEffects(registered, traitName, effects) {
    const irTrait = this.traitIndexFor(registered).byName.get(traitName)?.irTrait;
    return irTrait === void 0 ? void 0 : traitsEmbeddedByEffects(irTrait, effects);
  }
  async rerenderCallsiteCaptureChildren(registered, traitName, callsitePayload, entityData, entityId, emittedEvents, fetchedData, clientEffects, effectResults, user, clientEffectsByTrait, onPush, originClientId, now, visited = /* @__PURE__ */ new Set(), keepComposed = false, queueHold, locale, composedBy) {
    const children = this.callsiteCaptureChildrenByTrait.get(traitName);
    if (!children || children.size === 0) return;
    for (const childName of children) {
      if (composedBy !== void 0 && !composedBy.has(childName)) continue;
      if (visited.has(childName)) continue;
      visited.add(childName);
      const composedWith = keepComposed ? this.composedCallsitePayloads.get(childName) ?? callsitePayload : callsitePayload;
      const lifecycleEvent = registered.manager.repaintLifecycleEvent(childName);
      const [entry] = lifecycleEvent === void 0 ? [] : registered.manager.sendEvent(lifecycleEvent, {}, entityData, void 0, void 0, childName, user);
      if (!entry || !entry.result.executed) {
        await this.rerenderCallsiteCaptureChildren(
          registered,
          childName,
          composedWith,
          entityData,
          entityId,
          emittedEvents,
          fetchedData,
          clientEffects,
          effectResults,
          user,
          clientEffectsByTrait,
          onPush,
          originClientId,
          now,
          visited,
          true,
          queueHold,
          locale
        );
        continue;
      }
      xOrbitalLog.debug("callsite-capture-child:rerender", () => ({
        referrer: traitName,
        child: childName,
        lifecycleEvent,
        callsitePayload: JSON.stringify(composedWith)
      }));
      await this.executeEffects(
        registered,
        childName,
        entry.result.effects,
        {},
        entityData,
        entityId,
        emittedEvents,
        fetchedData,
        clientEffects,
        effectResults,
        user,
        clientEffectsByTrait,
        onPush,
        originClientId,
        composedWith,
        now,
        void 0,
        void 0,
        queueHold,
        locale
      );
      await this.rerenderCallsiteCaptureChildren(
        registered,
        childName,
        composedWith,
        entityData,
        entityId,
        emittedEvents,
        fetchedData,
        clientEffects,
        effectResults,
        user,
        clientEffectsByTrait,
        onPush,
        originClientId,
        now,
        visited,
        keepComposed,
        queueHold,
        locale,
        // Below a repainted child, only what its repainted transition composes.
        this.composedByEffects(registered, childName, entry.result.effects)
      );
    }
  }
  // ==========================================================================
  // Relation Population
  // ==========================================================================
  /**
   * Populate relation fields on entities
   *
   * For each field in `include`, find the relation field configuration and
   * fetch the related entity, attaching it to the parent entity.
   *
   * @param entities - Entities to populate
   * @param entityType - Entity type name
   * @param include - Relation field names to populate
   */
  /**
   * Validate that relation field values match their declared cardinality.
   * Called before create/update to ensure data integrity.
   */
  validateRelationCardinality(entityType, data) {
    for (const [, registered] of this.orbitals) {
      if (registered.entity.name !== entityType) continue;
      for (const field of registered.entity.fields ?? []) {
        if (field.type !== "relation") continue;
        if (field.name === void 0) continue;
        const fieldName = field.name;
        const value = data[fieldName];
        if (value === void 0 || value === null) continue;
        const cardinality = field.relation?.cardinality || "one";
        if (cardinality === "one" || cardinality === "many-to-one") {
          if (Array.isArray(value)) {
            throw new Error(
              `Cardinality violation: ${entityType}.${fieldName} has cardinality '${cardinality}' but received an array. Expected a single string ID.`
            );
          }
        } else if (cardinality === "many" || cardinality === "many-to-many" || cardinality === "one-to-many") {
          if (typeof value === "string") {
            data[fieldName] = [value];
          } else if (Array.isArray(value)) {
            const nonStrings = value.filter((v) => typeof v !== "string");
            if (nonStrings.length > 0) {
              throw new Error(
                `Cardinality violation: ${entityType}.${fieldName} has cardinality '${cardinality}' but array contains non-string values.`
              );
            }
          }
        }
      }
      break;
    }
  }
  /**
   * Enforce onDelete rules for relation fields pointing to the entity being deleted.
   * Scans all registered entities for relation fields targeting the given entity type,
   * finds records referencing the ID being deleted, and applies cascade/nullify/restrict.
   */
  async enforceOnDeleteRules(entityType, deletedId) {
    for (const [, registered] of this.orbitals) {
      const entity = registered.entity;
      const fields = entity.fields ?? [];
      for (const field of fields) {
        if (field.type !== "relation") continue;
        if (field.relation?.entity !== entityType) continue;
        if (field.name === void 0) continue;
        const fieldName = field.name;
        const onDelete = field.relation.onDelete || "restrict";
        const referringEntityType = entity.name;
        const allRecords = await this.persistence.list(referringEntityType);
        const affectedRecords = allRecords.filter((record) => {
          const fkValue = record[fieldName];
          if (typeof fkValue === "string") return fkValue === deletedId;
          if (Array.isArray(fkValue)) return fkValue.includes(deletedId);
          return false;
        });
        if (affectedRecords.length === 0) continue;
        switch (onDelete) {
          case "restrict":
            throw new Error(
              `Cannot delete ${entityType} ${deletedId}: ${affectedRecords.length} ${referringEntityType} record(s) reference it via ${field.name}. Rule: restrict.`
            );
          case "cascade":
            for (const record of affectedRecords) {
              const recordId = record.id;
              if (recordId) {
                await this.persistence.delete(referringEntityType, recordId);
              }
            }
            if (this.config.debug) {
              persistLog.debug("cascade-delete", {
                count: affectedRecords.length,
                entityType: referringEntityType
              });
            }
            break;
          case "nullify":
            for (const record of affectedRecords) {
              const recordId = record.id;
              if (recordId && field.name !== void 0) {
                const fieldName2 = field.name;
                const update = {};
                const fkValue = record[fieldName2];
                if (Array.isArray(fkValue)) {
                  update[fieldName2] = fkValue.filter((id) => id !== deletedId);
                } else {
                  update[fieldName2] = null;
                }
                await this.persistence.update(referringEntityType, recordId, update);
              }
            }
            if (this.config.debug) {
              persistLog.debug("nullify", {
                field: field.name,
                count: affectedRecords.length,
                entityType: referringEntityType
              });
            }
            break;
        }
      }
    }
  }
  async populateRelations(registered, entities, entityType, include, depth = 0, visited = /* @__PURE__ */ new Set()) {
    const maxDepth = 2;
    if (depth >= maxDepth || visited.has(entityType)) {
      if (this.config.debug) {
        persistLog.debug("populate:skip", {
          entityType,
          depth,
          visited: visited.has(entityType)
        });
      }
      return;
    }
    visited.add(entityType);
    const entityFields = this.entityFieldsFor(registered, entityType).filter(
      (f) => typeof f.name === "string" && f.name.length > 0
    );
    if (entityFields.length === 0) {
      if (this.config.debug) {
        persistLog.warn("populate:no-entity-def", { entityType });
      }
      return;
    }
    for (const includeField of include) {
      const relationField = entityFields.find((f) => {
        if (f.type !== "relation") return false;
        return f.name === includeField || f.name === `${includeField}Id` || f.name.replace(/Id$/, "") === includeField;
      });
      if (!relationField?.relation?.entity) {
        if (this.config.debug) {
          persistLog.warn("populate:no-relation-field", { includeField, entityType });
        }
        continue;
      }
      const foreignKeyField = relationField.name;
      let relatedEntityType = relationField.relation.entity;
      if (relationField.relation.entityId) {
        for (const registered2 of this.orbitals.values()) {
          if (registered2.entity.id === relationField.relation.entityId) {
            relatedEntityType = registered2.entity.name;
            break;
          }
        }
      }
      const cardinality = relationField.relation.cardinality || "one";
      const foreignKeyIds = /* @__PURE__ */ new Set();
      for (const entity of entities) {
        const fkValue = entity[foreignKeyField];
        if (fkValue && typeof fkValue === "string") {
          foreignKeyIds.add(fkValue);
        } else if (Array.isArray(fkValue)) {
          for (const id of fkValue) {
            if (id && typeof id === "string") {
              foreignKeyIds.add(id);
            }
          }
        }
      }
      if (foreignKeyIds.size === 0) continue;
      const relatedEntities = /* @__PURE__ */ new Map();
      for (const fkId of foreignKeyIds) {
        try {
          const related = await this.persistence.getById(relatedEntityType, fkId);
          if (related) {
            relatedEntities.set(fkId, related);
          }
        } catch (error) {
          if (this.config.debug) {
            persistLog.error("populate:fetch-related-error", {
              entityType: relatedEntityType,
              error: error instanceof Error ? error : String(error)
            });
          }
        }
      }
      const populatedFieldName = includeField.endsWith("Id") ? includeField.slice(0, -2) : includeField;
      const isSelfRef = relatedEntityType === entityType;
      const idsOf = (value) => {
        if (typeof value === "string") return [value];
        const ids = [];
        if (Array.isArray(value)) {
          for (const id of value) if (typeof id === "string") ids.push(id);
        }
        return ids;
      };
      const selfTree = isSelfRef && cardinality !== "one" && cardinality !== "many-to-one";
      if (selfTree) {
        let frontier = [...relatedEntities.values()];
        while (frontier.length > 0) {
          const next = [];
          for (const row of frontier) {
            for (const id of idsOf(row[foreignKeyField])) {
              if (relatedEntities.has(id)) continue;
              const loaded = await this.persistence.getById(relatedEntityType, id);
              if (loaded) {
                relatedEntities.set(id, loaded);
                next.push(loaded);
              }
            }
          }
          frontier = next;
        }
      }
      const hydrateClone = (id, ancestors) => {
        const related = relatedEntities.get(id);
        if (!related) return void 0;
        const copy = { ...related };
        if (selfTree) {
          const lineage = new Set(ancestors).add(id);
          copy[foreignKeyField] = idsOf(related[foreignKeyField]).filter((childId) => !lineage.has(childId)).map((childId) => hydrateClone(childId, lineage)).filter((child) => child !== void 0);
        } else if (isSelfRef) {
          copy[foreignKeyField] = [];
        }
        return copy;
      };
      for (const entity of entities) {
        const fkValue = entity[foreignKeyField];
        const lineage = new Set(typeof entity.id === "string" ? [entity.id] : []);
        if (cardinality === "one" || cardinality === "many-to-one") {
          if (typeof fkValue === "string" && relatedEntities.has(fkValue)) {
            Object.defineProperty(entity, populatedFieldName, {
              value: hydrateClone(fkValue, lineage),
              writable: true,
              enumerable: true,
              configurable: true
            });
          }
        } else {
          const fkIds = idsOf(fkValue).filter((id) => !lineage.has(id) && relatedEntities.has(id));
          if (Array.isArray(fkValue) || fkIds.length > 0) {
            Object.defineProperty(entity, populatedFieldName, {
              value: fkIds.map((id) => hydrateClone(id, lineage)).filter((child) => child !== void 0),
              writable: true,
              enumerable: true,
              configurable: true
            });
          }
        }
      }
      if (this.config.debug) {
        persistLog.debug("populate:done", {
          field: populatedFieldName,
          count: entities.length,
          entityType
        });
      }
    }
  }
  // ==========================================================================
  // Express Router
  // ==========================================================================
  /**
   * Create Express router for orbital API endpoints
   *
   * All data access goes through trait events with guards.
   * No direct CRUD routes - use events with `fetch` effects.
   *
   * Routes:
   * - GET  /              - List registered orbitals
   * - GET  /:orbital      - Get orbital info and current states
   * - GET  /:orbital/entities/:entityType - Full mock-store row set (verification/tooling only)
   * - POST /:orbital/events - Send event to orbital (includes data from `fetch` effects)
   */
  router() {
    if (!isNodeEnv()) {
      throw new Error(
        "OrbitalServerRuntime.router() is Node-only (uses Express). For in-browser use, mount <BrowserPlayground> from @almadar/ui instead."
      );
    }
    const { Router } = nodeRequire("express");
    const router = Router();
    router.get("/", (_req, res) => {
      const orbitals = Array.from(this.orbitals.entries()).map(
        ([name, reg]) => ({
          name,
          entity: reg.entity?.name,
          traits: (reg.traits || []).map((t) => t.name)
        })
      );
      res.json({ success: true, orbitals });
    });
    router.get("/inputs", (_req, res) => {
      res.json({ success: true, inputs: this.listExternalInputs() });
    });
    router.post("/:orbital/inputs", async (req, res, next) => {
      try {
        const orbitalName = req.params.orbital;
        const user = await this.viewerOf(req.authUser);
        const body = req.body;
        const result = await this.dispatchExternalInput(orbitalName, {
          targetTrait: String(body.targetTrait ?? ""),
          event: String(body.event ?? ""),
          ...body.payload !== void 0 ? { payload: body.payload } : {},
          ...body.entityId !== void 0 ? { entityId: body.entityId } : {},
          user
        });
        const refused = result.rejections?.some((r) => r.code === "not-an-external-input") === true;
        res.status(refused ? 403 : 200).json(result);
      } catch (error) {
        next(error);
      }
    });
    router.get("/:orbital", async (req, res, next) => {
      try {
        const orbitalName = req.params.orbital;
        const registered = await this.resolveRegisteredOrbital(orbitalName);
        if (!registered) {
          res.status(404).json({ success: false, error: "Orbital not found" });
          return;
        }
        const states = {};
        for (const [name, state] of registered.manager.getAllStates()) {
          states[name] = state.currentState;
        }
        res.json({
          success: true,
          orbital: {
            name: orbitalName,
            entity: registered.entity,
            traits: registered.traits.map((t) => ({
              name: t.name,
              currentState: states[t.name],
              states: (t.stateMachine?.states || []).map((s) => s.name),
              events: [...new Set((t.stateMachine?.transitions || []).map((tr) => tr.event))]
            }))
          }
        });
      } catch (error) {
        next(error);
      }
    });
    router.get("/:orbital/entities/:entityType", (req, res, next) => {
      const orbitalName = req.params.orbital;
      const entityType = req.params.entityType;
      this.resolveRegisteredOrbital(orbitalName).then((registered) => {
        if (!registered) {
          res.status(404).json({ success: false, error: "Orbital not found" });
          return;
        }
        return this.persistence.list(entityType).then((rows) => {
          res.json({ success: true, entityType, rows });
        });
      }).catch(next);
    });
    router.post(
      "/:orbital/events",
      async (req, res, next) => {
        const wantsStream = req.query["stream"] === "true" || (req.headers["accept"] ?? "").includes("text/event-stream");
        if (!wantsStream) {
          try {
            const orbitalName = req.params.orbital;
            const user = await this.viewerOf(req.authUser);
            const result = await this.processOrbitalEvent(orbitalName, { ...req.body, user });
            res.json(result);
          } catch (error) {
            next(error);
          }
          return;
        }
        const { setupSSE, sendSSEEvent, sendSSEDone, closeSSE } = await import('@almadar/server');
        setupSSE(res);
        try {
          const orbitalName = req.params.orbital;
          const user = await this.viewerOf(req.authUser);
          const result = await this.processOrbitalEvent(orbitalName, { ...req.body, user }, (item) => {
            sendSSEEvent(res, { type: item.type, data: item.data, timestamp: Date.now() });
          });
          sendSSEEvent(res, { type: "complete", data: result, timestamp: Date.now() });
          sendSSEDone(res);
          closeSSE(res);
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          sendSSEEvent(res, { type: "error", data: { message }, timestamp: Date.now() });
          sendSSEDone(res);
          closeSSE(res);
        }
      }
    );
    return router;
  }
  // ==========================================================================
  // Direct API (for programmatic use)
  // ==========================================================================
  /**
   * Get the event bus for manual event emission
   */
  getEventBus() {
    return this.eventBus;
  }
  /** Observe every executed hop (listener deliveries included) on every orbital; returns the unsubscribe. */
  observeTransitions(observer) {
    this.transitionObservers.add(observer);
    return () => {
      this.transitionObservers.delete(observer);
    };
  }
  /**
   * Get state for a specific orbital/trait
   */
  getState(orbitalName, traitName) {
    const registered = this.orbitals.get(orbitalName);
    if (!registered) return void 0;
    if (traitName) {
      return registered.manager.getState(traitName);
    }
    const states = {};
    for (const [name, state] of registered.manager.getAllStates()) {
      states[name] = state;
    }
    return states;
  }
  /**
   * List registered orbitals
   */
  listOrbitals() {
    return Array.from(this.orbitals.keys());
  }
  /**
   * Check if an orbital is registered
   */
  hasOrbital(name) {
    return this.orbitals.has(name);
  }
  /**
   * Get information about active ticks
   */
  getActiveTicks() {
    return this.tickBindings.map((binding) => ({
      orbital: binding.orbitalName,
      trait: binding.traitName,
      tick: binding.tick.name,
      interval: binding.tick.interval,
      hasGuard: !!binding.tick.guard
    }));
  }
  /** Halt every registered tick's shared clock. Delegates to `TickScheduler.pause`. */
  pauseTicks() {
    this.tickScheduler.pause();
  }
  /** Resume ticks halted by `pauseTicks()`. Delegates to `TickScheduler.resume`. */
  resumeTicks() {
    this.tickScheduler.resume();
  }
  /** True while ticks are paused. Delegates to `TickScheduler.isPaused`. */
  areTicksPaused() {
    return this.tickScheduler.isPaused;
  }
};
function createOrbitalServerRuntime(config) {
  return new OrbitalServerRuntime(config);
}
function foldRelayHop(into, hop) {
  if (hop.clientEffects !== void 0 && hop.clientEffects.length > 0) {
    into.clientEffects = [...into.clientEffects ?? [], ...hop.clientEffects];
  }
  if (hop.clientEffectsByTrait !== void 0 && hop.clientEffectsByTrait.length > 0) {
    into.clientEffectsByTrait = [...into.clientEffectsByTrait ?? [], ...hop.clientEffectsByTrait];
  }
  if (hop.entityByTrait !== void 0) {
    into.entityByTrait = { ...into.entityByTrait ?? {}, ...hop.entityByTrait };
  }
  into.states = { ...into.states, ...hop.states };
  for (const emitted of hop.emittedEvents) {
    into.emittedEvents.push({ ...emitted, source: { ...emitted.source ?? {}, dispatched: true } });
  }
}

export { OrbitalServerRuntime, createOrbitalServerRuntime };
